import { keyframes } from "@mui/material";

export const PAN_THRESHOLD = 6;
export const ROTATION_DEAD_ZONE = 16;
export const HINT_DELAY = 1250;
export const ENTRANCE_SCALE_DURATION = 300;
export const ENTRANCE_OPACITY_DURATION = 250;
export const ENTRANCE_TOTAL_DURATION = 350;
export const TOOLTIP_FADE = 250;
export const INDICATOR_PROXIMITY = 21;
export const PULSE_DURATION = 1000;
export const INDICATOR_CIRCLE_RADIUS = 18.8;

export const ENTRANCE_SCALE_BEZIER = [0.34, 1.4, 0.64, 1] as const;
export const ENTRANCE_OPACITY_BEZIER = [0.0, 0.0, 0.58, 1.0] as const;

export const orbitDirSwitch = keyframes`
    0% { transform: rotate(-60deg); }
    50% { transform: rotate(60deg); }
    100% { transform: rotate(-60deg); }
`;

export const orbitOpacity = keyframes`
    0% { opacity: 0.4; }
    25% { opacity: 1; }
    50% { opacity: 0.4; }
    75% { opacity: 1; }
    100% { opacity: 0.4; }
`;

export const ahCw = keyframes`
    0% { opacity: 1; }
    40% { opacity: 1; }
    40.01% { opacity: 0; }
    90% { opacity: 0; }
    90.01% { opacity: 1; }
    100% { opacity: 1; }
`;

export const ahCcw = keyframes`
    0% { opacity: 0; }
    40% { opacity: 0; }
    40.01% { opacity: 1; }
    90% { opacity: 1; }
    90.01% { opacity: 0; }
    100% { opacity: 0; }
`;

export const hintIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

export const hintOut = keyframes`
    from { opacity: 1; }
    to { opacity: 0; }
`;

export type EntrancePhase = "idle" | "hidden" | "entering" | "placed";

export interface NavigationState {
    entrancePhase: EntrancePhase;
    isTrackingRotation: boolean;
    hasRotated: boolean;
    hintsCanShow: boolean;
    hintsMounted: boolean;
    hintsExiting: boolean;
    showPulse: boolean;
    mapPanning: boolean;
    tooltipVisible: boolean;
    tooltipText: "navigate" | "confirm";
    clickOrigin: { x: number; y: number } | null;
    mousedownOrigin: { x: number; y: number } | null;
    entranceStartTime: number;
    animFrameId: number | null;
    hintTimerId: ReturnType<typeof setTimeout> | null;
}

export function createInitialNavigationState(): NavigationState {
    return {
        entrancePhase: "idle",
        isTrackingRotation: false,
        hasRotated: false,
        hintsCanShow: false,
        hintsMounted: false,
        hintsExiting: false,
        showPulse: false,
        mapPanning: false,
        tooltipVisible: false,
        tooltipText: "navigate",
        clickOrigin: null,
        mousedownOrigin: null,
        entranceStartTime: 0,
        animFrameId: null,
        hintTimerId: null,
    };
}

function cubicBezier(t: number, p1: number, p2: number, p3: number, p4: number): number {
    const cx = 3 * p1;
    const bx = 3 * (p3 - p1) - cx;
    const ax = 1 - cx - bx;
    const cy = 3 * p2;
    const by = 3 * (p4 - p2) - cy;
    const ay = 1 - cy - by;

    let x = t;
    for (let i = 0; i < 8; i++) {
        const xEst = ((ax * x + bx) * x + cx) * x - t;
        if (Math.abs(xEst) < 1e-6) {
            break;
        }
        const dxEst = (3 * ax * x + 2 * bx) * x + cx;
        if (Math.abs(dxEst) < 1e-6) {
            break;
        }
        x -= xEst / dxEst;
    }
    return ((ay * x + by) * x + cy) * x;
}

export function entranceScaleAt(progress: number): number {
    const t = Math.min(1, Math.max(0, progress));
    const eased = cubicBezier(t, ...ENTRANCE_SCALE_BEZIER);
    return 4 + (1 - 4) * eased;
}

export function entranceOpacityAt(progress: number): number {
    const t = Math.min(1, Math.max(0, progress));
    const eased = cubicBezier(t, ...ENTRANCE_OPACITY_BEZIER);
    return eased;
}
